export const reducedMotion = '(prefers-reduced-motion: reduce)';
export const highContrast = '(prefers-contrast: more)';
export const reducedTransparency = '(prefers-reduced-transparency: reduce)';

export const mobileSmall = '(max-width: 320px)';
/**
 * 모바일 상한 폭(px) — 폭 기준 모바일 판정의 **단일 소스**.
 *
 * CSS 는 아래 `mobile` 로, JS 는 이 숫자로 같은 축을 잰다. 숫자를 따로 적으면 그 사이 구간이
 * "CSS 는 모바일인데 JS 는 데스크톱" 인 사각지대가 된다 — 도깨비 패널이 481~768px 에서
 * 바텀시트를 잃은 원인이 그것이었다. 새 판정을 쓸 땐 리터럴 대신 이 상수를 import 할 것.
 *
 * 🔴 `matchMedia(mobile)` 파생은 채택하지 않는다 — vitest node 프로젝트에는 `matchMedia` 가
 * 없어(테스트가 심어둔 mock 을 되읽는) 항진명제가 된다. 숫자 비교여야 경계값으로 때릴 수 있다.
 */
export const MOBILE_MAX_WIDTH = 768;
/**
 * 가로로 누운 폰의 높이 상한(px) — 폭이 768 을 넘는 폰을 잡는 **두 번째 축**.
 *
 * 폭만으로 가르면 갤럭시 S25+ 가로(891px)·iPhone Pro Max 가로(956px)가 데스크톱으로 갈려
 * 폰에 드래그·리사이즈 창 매니저가 그대로 뜬다. 가로 폰의 높이는 412~440px 인데 태블릿
 * 가로는 744~834px 이라, 그 사이 300px 공백에 임계를 두면 폰만 걸린다.
 *
 * `pointer: coarse` 를 반드시 동반한다 — 높이 단독이면 창을 납작하게 줄인 데스크톱 브라우저가
 * 모바일 셸로 넘어간다. 기기별 판정표는 `__tests__/mobileShellLandscape.test.ts` 참조.
 */
export const MOBILE_MAX_LANDSCAPE_HEIGHT = 500;
export const mobile = `(max-width: ${MOBILE_MAX_WIDTH}px), (max-height: ${MOBILE_MAX_LANDSCAPE_HEIGHT}px) and (pointer: coarse)`;
export const tablet = '(min-width: 769px) and (max-width: 1024px)';
export const desktop = '(min-width: 1025px)';

/**
 * 데스크톱 헤더의 폭 예산 임계 두 개 — 모바일이 아니지만 한 줄이 안 되는 구간.
 *
 * 실측(2026-08-27, Storybook + Playwright): nav 502px · 알림 벨 ~50px · 오늘 휴가 배지 96px
 * (0명, 팀 칩 있으면 152px) · 리더보드 배너 340~480px(데이터 의존) · 좌우 패딩 48px · gap 16px×3.
 * 전부 더하면 1084~1224px 이라 그 아래에서 `headerRight` 의 flexWrap 이 발화해 휴가 배지와
 * 배너가 세로로 쌓였다. 두 단계로 나눠 내준다:
 *
 *   `headerTight`(≤1250px)   — 스코프 라벨의 "퀘스트" 접미를 숨긴다. 502→367px(-135).
 *                              아이콘이 남아 구분되고 aria-label 이 전체 이름을 보존한다.
 *   `headerCramped`(≤1000px) — 오늘 휴가 배지를 숨긴다(-112px, gap 포함). 접미 축약만으로는
 *                              900px 대에서 여전히 모자랐다(필요 ~949px vs 실제 937px).
 *
 * 두 단계를 다 내주면 900px 대에서도 배너가 말줄임 없이 자연폭으로 한 줄에 들어간다.
 * 그 아래는 여전히 flexWrap 이 최후 수단으로 남는다 — 잘리는 것보다 접히는 게 낫다.
 */
export const HEADER_TIGHT_MAX_WIDTH = 1250;
export const headerTight = `(max-width: ${HEADER_TIGHT_MAX_WIDTH}px)`;
export const HEADER_CRAMPED_MAX_WIDTH = 1000;
export const headerCramped = `(max-width: ${HEADER_CRAMPED_MAX_WIDTH}px)`;

/**
 * 짧은 세로 데스크톱 크롬 — 14인치 노트북 브라우저 뷰포트(~880–920)를 포함한다.
 * 800px 는 그 기기를 놓친다. pointer:fine 은 가로 폰(폭>768·높이<500·coarse)이
 * 데스크톱 패딩을 덮어쓰지 않게 한다.
 *
 * 960 은 목표를 넘겨 잡았다(#2030 최초값). 1080p 데스크톱 모니터는 브라우저 크롬을
 * 빼면 뷰포트가 ~950–970 이라, 세로가 넉넉한 데스크톱에서도 상시 compact 로 접혔다.
 * 경계가 그 대역 한가운데라 창 크기·줌을 조금만 건드려도 크롬이 깜빡였다.
 * 930 은 두 기기 대역 사이에 있다 — 14인치(880–920)는 여전히 잡고, 1080p(950–970)는 놓는다.
 * 팀 기준기(14" MacBook Pro 논리 1512×982)의 크롬 창 모드 뷰포트가 858 이라 72px 여유가 남는다
 * — `windowPresetFitsLaptop.test.ts` 의 기준 플레인 산식과 같은 출처다.
 */
export const COMPACT_CHROME_MAX_HEIGHT = 930;
export const shortHeight = `(max-height: ${COMPACT_CHROME_MAX_HEIGHT}px)`;
export const tallHeight = `(min-height: ${COMPACT_CHROME_MAX_HEIGHT + 1}px)`;
export const compactChrome = `(max-height: ${COMPACT_CHROME_MAX_HEIGHT}px) and (min-width: ${MOBILE_MAX_WIDTH + 1}px) and (pointer: fine)`;

/**
 * 포인터 능력 질의 — 폭이 아니라 입력 장치가 기준이다.
 *
 * `noHover` — hover 로만 드러나는 어포던스(hover 시 노출되는 ✕ 등)를 상시 노출로 되돌릴 때.
 *   폭 기준으로 하면 터치 노트북이 빠진다. "hover 를 못 하는 환경" 자체가 조건이므로 이게 정확.
 * `coarsePointer` — 탭 타깃 최소 크기를 키울 때. 손가락은 마우스보다 정밀도가 낮다.
 * `hoverFine` — hover 로만 드러나는 규칙을 **감쌀** 때. base 를 `@media (hover: none)` 으로
 *   되돌리는 방식은 무력하다 — 미디어쿼리는 specificity 를 더하지 않으므로 top-level
 *   `:hover` 셀렉터가 항상 이기고, 터치 기기의 sticky hover 로 탭 한 번에 드러난다.
 */
export const noHover = '(hover: none)';
export const coarsePointer = '(pointer: coarse)';
export const hoverFine = '(hover: hover) and (pointer: fine)';
